import { useLiveQuery } from "dexie-react-hooks";
import { CampoBusca } from "../componentes/CampoBusca.js";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CHAVES, banco, gravarAjuste, lerAjuste } from "../dados/banco.js";

/// Escolha do checklist.
///
/// Aqui só se escolhe — a inspeção nasce no botão "Nova ação" da tela inicial. A
/// escolha fica fixada no aparelho, então quem vai aplicar o mesmo checklist a
/// manhã inteira passa por esta tela uma vez só.

export function NovaInspecao() {
  const [busca, setBusca] = useState("");
  const navegar = useNavigate();

  const checklists = useLiveQuery(() => banco.checklists.toArray(), [], []);
  const obras = useLiveQuery(() => banco.obras.toArray(), [], []);
  const obraSelecionada = useLiveQuery(
    () => lerAjuste<string | null>(CHAVES.obraSelecionada, null),
    [],
    null,
  );
  const escolhido = useLiveQuery(
    () => lerAjuste<string | null>(CHAVES.checklistSelecionado, null),
    [],
    null,
  );
  const obraAtiva =
    obraSelecionada ?? (obras.length === 1 ? obras[0]!.id : null);

  const { ini, dinamicos } = useMemo(() => {
    const alvo = busca.trim().toLowerCase();
    const filtra = (c: { nome: string; codigo?: string | null }) =>
      !alvo ||
      c.nome.toLowerCase().includes(alvo) ||
      (c.codigo ?? "").toLowerCase().includes(alvo);
    return {
      ini: checklists.filter((c) => c.tipo === "INI").filter(filtra),
      dinamicos: checklists
        .filter((c) => c.tipo === "DINAMICO")
        .filter(filtra)
        .sort((a, b) =>
          (a.codigo ?? a.nome).localeCompare(b.codigo ?? b.nome, "pt-BR"),
        ),
    };
  }, [checklists, busca]);

  async function escolher(checklistId: string) {
    await gravarAjuste(CHAVES.checklistSelecionado, checklistId);
    navegar("/");
  }

  if (!obraAtiva) {
    return (
      <div className="cartao p-6 text-center">
        <p className="font-semibold text-slate-700">
          Selecione uma obra primeiro
        </p>
        <button onClick={() => navegar("/")} className="botao-primario mt-4">
          Voltar ao início
        </button>
      </div>
    );
  }

  return (
    <section>
      <button
        onClick={() => navegar("/")}
        className="mb-3 text-sm font-medium text-marinho"
      >
        ← Voltar
      </button>

      <h2 className="mb-1 text-lg font-bold text-slate-900">
        Escolher checklist
      </h2>
      <p className="mb-4 text-sm text-slate-600">
        Depois de escolher, toque em "Nova ação" na tela inicial para começar.
      </p>

      <div className="mb-4">
        <CampoBusca
          valor={busca}
          aoMudar={setBusca}
          placeholder="Buscar checklist por nome ou código"
        />
      </div>

      {ini.length > 0 && (
        <div className="mb-6">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Inspeção, notificação e interdição
          </h3>
          <ul className="grid gap-2 lg:grid-cols-2 2xl:grid-cols-3">
            {ini.map((c) => (
              <li key={c.id}>
                <button
                  onClick={() => void escolher(c.id)}
                  className={`cartao w-full p-4 text-left ${
                    c.id === escolhido
                      ? "border-limao bg-limao-100"
                      : "border-limao/40 bg-limao-50 hover:border-limao"
                  }`}
                >
                  <p className="font-semibold text-marinho-900">{c.nome}</p>
                  <p className="mt-0.5 text-xs text-slate-600">
                    {c.id === escolhido
                      ? "Escolhido"
                      : "Formulário principal de campo"}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Checklists ({dinamicos.length})
      </h3>
      {dinamicos.length === 0 ? (
        <p className="cartao p-6 text-center text-sm text-slate-500">
          {busca
            ? "Nenhum checklist encontrado."
            : "Os checklists ainda não foram baixados."}
        </p>
      ) : (
        <ul className="grid gap-2 lg:grid-cols-2 2xl:grid-cols-3">
          {dinamicos.map((c) => (
            <li key={c.id}>
              <button
                onClick={() => void escolher(c.id)}
                className={`cartao w-full p-4 text-left ${
                  c.id === escolhido
                    ? "border-marinho bg-marinho-50"
                    : "hover:border-marinho"
                }`}
              >
                <div className="flex items-start gap-2">
                  {c.codigo && (
                    <span className="etiqueta shrink-0 bg-marinho-50 font-mono text-marinho">
                      {c.codigo}
                    </span>
                  )}
                  {c.revisao && (
                    <span className="etiqueta bg-slate-100 text-slate-500">
                      {c.revisao}
                    </span>
                  )}
                  {c.id === escolhido && (
                    <span className="etiqueta ml-auto bg-marinho text-white">
                      Escolhido
                    </span>
                  )}
                </div>
                <p className="mt-1.5 font-medium text-slate-800">{c.nome}</p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
